/**
 * DTO → view-model mapper for genetic variants (the ``genetic-info``
 * endpoint family). Kept parallel to `feature.ts` / `diagnosis.ts` so
 * future back-end field additions surface as localised diffs.
 *
 * Narrowing policy:
 *   - Unknown enum values become ``null`` (not a bogus default). The UI
 *     renders these as ``—`` so a corrupted field never silently
 *     misinforms interpretation.
 *   - ``associated_conditions`` and ``acmg_criteria`` are normalised to
 *     arrays so consumers can ``.map()`` without null-guards.
 *
 * The live PhenoTips-style payload uses camelCase and omits the BFF's
 * ``gene`` / ``variant`` HGVS pair in favour of ``geneSymbol``, locus
 * fields, and partial ``dateObserved`` objects — we normalise both shapes
 * here so list GETs and PUT responses map consistently.
 */

import type { components } from '@/api/client';
import type {
  GeneticVariantViewModel,
  VariantInheritance,
  VariantInterpretation,
  VariantType,
  VariantZygosity,
} from '@/features/patient-record/types';

import { narrowEnum, nullableString } from '@/api/mappers/_shared';

type VariantDto = components['schemas']['Variant'];

const KNOWN_TYPES: ReadonlySet<VariantType> = new Set<VariantType>([
  'SNV',
  'CNV',
  'INDEL',
  'SV',
  'other',
]);

const KNOWN_ZYGOSITIES: ReadonlySet<VariantZygosity> = new Set<VariantZygosity>([
  'Heterozygous',
  'Homozygous',
  'Hemizygous',
  'Compound Heterozygous',
  'Unknown',
]);

const KNOWN_INTERPRETATIONS: ReadonlySet<VariantInterpretation> =
  new Set<VariantInterpretation>([
    'Pathogenic',
    'Likely Pathogenic',
    'VUS',
    'Likely Benign',
    'Benign',
  ]);

function asRecord(dto: VariantDto): Record<string, unknown> {
  return dto as unknown as Record<string, unknown>;
}

function pick<T>(r: Record<string, unknown>, snake: string, camel: string): T | undefined {
  const a = r[snake];
  if (a !== undefined && a !== null) return a as T;
  const b = r[camel];
  if (b !== undefined && b !== null) return b as T;
  return undefined;
}

function readStringList(r: Record<string, unknown>, snake: string, camel: string): string[] {
  const raw = pick<unknown>(r, snake, camel);
  if (!Array.isArray(raw)) return [];
  return raw.filter((x): x is string => typeof x === 'string');
}

/** Same partial-date triple as demographics / ``PartialDate`` on the BE. */
function formatPartialDateTriple(
  o: { year?: unknown; month?: unknown; day?: unknown } | null | undefined,
): string | null {
  if (o === null || o === undefined) return null;
  if (typeof o.year !== 'number') return null;
  const parts = [String(o.year).padStart(4, '0')];
  if (typeof o.month === 'number') parts.push(String(o.month).padStart(2, '0'));
  if (typeof o.month === 'number' && typeof o.day === 'number') {
    parts.push(String(o.day).padStart(2, '0'));
  }
  return parts.join('-');
}

function readDateObserved(r: Record<string, unknown>): string | null {
  const a = pick<unknown>(r, 'date_observed', 'dateObserved');
  if (typeof a === 'string') return nullableString(a);
  if (a !== null && typeof a === 'object' && !Array.isArray(a)) {
    return formatPartialDateTriple(a as { year?: unknown; month?: unknown; day?: unknown });
  }
  return null;
}

function readGene(r: Record<string, unknown>): string {
  const g = pick<string | undefined>(r, 'gene', 'geneSymbol');
  return nullableString(g) ?? '';
}

function readVariantNotation(r: Record<string, unknown>): string {
  const hgvs =
    nullableString(r.variant as unknown) ??
    nullableString(r.hgvs as unknown) ??
    nullableString(r.cdna as unknown) ??
    nullableString(r.protein as unknown);
  if (hgvs !== null) return hgvs;

  const chrRaw = nullableString(r.chromosome as unknown);
  if (chrRaw === null) return '';
  const chr = chrRaw.toLowerCase().startsWith('chr') ? chrRaw : `chr${chrRaw}`;
  const start = r.start;
  const end = r.end;
  if (typeof start === 'number' && typeof end === 'number') {
    return `${chr}:${start}-${end}`;
  }
  if (typeof start === 'number') {
    return `${chr}:${start}`;
  }
  return chr;
}

function readVariantType(r: Record<string, unknown>): VariantType | null {
  const raw = pick<string | undefined>(r, 'variant_type', 'variantType');
  if (raw === undefined) return null;
  const s = String(raw).trim();
  if (s.length === 0) return null;
  const lower = s.toLowerCase();
  if (lower === 'na' || lower === 'n/a') return 'other';

  const canon: Record<string, VariantType> = {
    snv: 'SNV',
    cnv: 'CNV',
    indel: 'INDEL',
    sv: 'SV',
    other: 'other',
    SNV: 'SNV',
    CNV: 'CNV',
    INDEL: 'INDEL',
    SV: 'SV',
    OTHER: 'other',
  };
  const mapped = canon[s] ?? canon[lower];
  if (mapped !== undefined) return mapped;
  return narrowEnum<VariantType>(s, KNOWN_TYPES, null);
}

const ZYGOSITY_SYNONYMS: Readonly<Record<string, VariantZygosity>> = {
  het: 'Heterozygous',
  heterozygous: 'Heterozygous',
  hom: 'Homozygous',
  homozygous: 'Homozygous',
  hemizygous: 'Hemizygous',
  hemizygote: 'Hemizygous',
  'compound het': 'Compound Heterozygous',
  'compound heterozygous': 'Compound Heterozygous',
  compound: 'Compound Heterozygous',
  unknown: 'Unknown',
  unk: 'Unknown',
};

function readZygosity(r: Record<string, unknown>): VariantZygosity | null {
  const z = r.zygosity;
  const fromDirect =
    typeof z === 'string'
      ? narrowEnum<VariantZygosity>(z.trim(), KNOWN_ZYGOSITIES, null)
      : null;
  if (fromDirect !== null) return fromDirect;

  const snv = nullableString(r.zygositySnv as unknown);
  if (snv === null) return null;
  const syn = ZYGOSITY_SYNONYMS[snv.toLowerCase()];
  if (syn !== undefined) return syn;
  return narrowEnum<VariantZygosity>(snv, KNOWN_ZYGOSITIES, null);
}

const INTERPRETATION_SYNONYMS: Readonly<Record<string, VariantInterpretation>> = {
  pathogenic: 'Pathogenic',
  likely_pathogenic: 'Likely Pathogenic',
  'likely pathogenic': 'Likely Pathogenic',
  vus: 'VUS',
  likely_benign: 'Likely Benign',
  'likely benign': 'Likely Benign',
  benign: 'Benign',
};

function readInterpretation(r: Record<string, unknown>): VariantInterpretation | null {
  const raw = r.interpretation;
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (trimmed.length === 0) return null;
  const lower = trimmed.toLowerCase();
  // BE sentinels for "not assessed" — render as "no interpretation".
  if (lower === 'na' || lower === 'n/a') return null;
  const syn = INTERPRETATION_SYNONYMS[lower];
  if (syn !== undefined) return syn;
  return narrowEnum<VariantInterpretation>(trimmed, KNOWN_INTERPRETATIONS, null);
}

/** Lab name lives at `source.label` when `source.type === 'lab'` on live PT. */
function readLabName(r: Record<string, unknown>): string | null {
  const direct = nullableString(pick(r, 'lab_name', 'labName'));
  if (direct !== null) return direct;
  const src = r.source;
  if (src !== null && typeof src === 'object' && !Array.isArray(src)) {
    const s = src as { type?: unknown; label?: unknown };
    if (s.type === 'lab') return nullableString(s.label);
  }
  return null;
}

const KNOWN_INHERITANCE: ReadonlySet<VariantInheritance> =
  new Set<VariantInheritance>([
    'de_novo_germline',
    'de_novo_s_mosaicism',
    'paternal',
    'maternal',
    'biparental',
    'unknown',
  ]);

/**
 * Drop the BE `na` sentinel and narrow free text into the `InheritanceOptions`
 * union. Unknown / legacy values from older payloads collapse to `null` so
 * the UI doesn't render junk.
 */
function readInheritance(r: Record<string, unknown>): VariantInheritance | null {
  const v = nullableString(r.inheritance as unknown);
  if (v === null) return null;
  const lower = v.toLowerCase();
  if (lower === 'na' || lower === 'n/a') return null;
  return narrowEnum<VariantInheritance>(lower, KNOWN_INHERITANCE, null);
}

/** Maps a single `Variant` DTO to `GeneticVariantViewModel`. */
export function mapGeneticVariant(dto: VariantDto): GeneticVariantViewModel {
  const r = asRecord(dto);
  return {
    id: dto.id,
    gene: readGene(r),
    variant: readVariantNotation(r),
    variantType: readVariantType(r),
    zygosity: readZygosity(r),
    interpretation: readInterpretation(r),
    inheritance: readInheritance(r),
    associatedConditions: readStringList(r, 'associated_conditions', 'associatedConditions'),
    testOrderId: nullableString(pick(r, 'test_order_id', 'testOrderId')),
    reportDate: nullableString(pick(r, 'report_date', 'reportDate')),
    labName: readLabName(r),
    dateObserved: readDateObserved(r),
    acmgCriteria: readStringList(r, 'acmg_criteria', 'acmgCriteria'),
    comments: nullableString(r.comments as unknown),
    createdAt: nullableString(pick(r, 'created_at', 'createdAt')),
    raw: r,
  };
}

/** Maps a list of `Variant` DTOs. */
export function mapGeneticVariants(
  dtos: readonly VariantDto[],
): GeneticVariantViewModel[] {
  return dtos.map(mapGeneticVariant);
}
